import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'
import range from 'lodash/range'

import ExpandAltOutlinedIcon from '@ant-design/icons/ExpandAltOutlined'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'
import { toTitleCase } from '@shared/utils'

import AssessmentDetailsModal from '@pages/Assessments/AssessmentDetailsModal'
import LinearProgress from '@components/LinearProgress'
import Pagination from '@components/Pagination'
import RenderControl from '@components/RenderControl'
import Tile from '@components/Tile'

import { Empty, IconCircleButton } from '../ProfileTab.utils'
import { useAssessments, useAssessmentsFilters } from './Assessments.hooks'

const LIMIT = 4

export default function Assessments() {
  const [isAsmtDetailsOpen, setIsAsmtDetailsOpen] = useState(false)
  const [selectedAsmtId, setSelectedAsmtId] = useState(null)

  const { data, isRefreshing, isPending } = useAssessments({ order: 'desc', limit: LIMIT })
  const [{ asmtsExpanded }, updateFilters] = useAssessmentsFilters()

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Tile
      title="Assessments"
      refreshing={isRefreshing}
      action={
        <IconCircleButton disabled={(data?.length ?? 0) === 0} size="small" onClick={() => updateFilters({ asmtsExpanded: true })}>
          <ExpandAltOutlinedIcon />
        </IconCircleButton>
      }
      sx={{ height: '100%' }}
    >
      <ExpandedDialog
        open={asmtsExpanded}
        onClose={() => updateFilters({ asmtsExpanded: false })}
        onSelect={(id) => {
          setSelectedAsmtId(id)
          setIsAsmtDetailsOpen(true)
        }}
      />
      <AssessmentDetailsModal
        assessmentId={selectedAsmtId}
        open={Boolean(selectedAsmtId && isAsmtDetailsOpen)}
        onClose={() => setIsAsmtDetailsOpen(false)}
      />
      {showLoading && <Loading />}
      {showEmpty && <Empty />}
      {showData && (
        <List disablePadding data-testid="assessments">
          {data?.map((asmt, i) => (
            <Item
              key={asmt.id}
              divider={i < data.length - 1}
              asmt={asmt}
              onClick={() => {
                setSelectedAsmtId(asmt.id)
                setIsAsmtDetailsOpen(true)
              }}
            />
          ))}
        </List>
      )}
    </Tile>
  )
}

function Item({ divider, asmt, onClick }) {
  return (
    <ListItemButton divider={divider} onClick={onClick}>
      <Stack direction="row" sx={{ gap: 2, justifyContent: 'space-between', width: '100%' }}>
        <Typography noWrap>{asmt.name}</Typography>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-end' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {toTitleCase(asmt.status)}
          </Typography>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{dayjs(asmt.createdAt).format('L')}</Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

Item.Loading = function ({ divider }) {
  return (
    <ListItemButton divider={divider}>
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'space-between', width: '100%' }}>
        <Typography>
          <Skeleton width={150} />
        </Typography>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Typography>
            <Skeleton width={80} />
          </Typography>
          <Typography>
            <Skeleton width={80} />
          </Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

function Loading() {
  return range(0, LIMIT).map((i) => <Item.Loading key={i} divider={i < LIMIT - 1} />)
}

function ExpandedDialog({ open, onClose, onSelect }) {
  const [{ asmtsOrder }, updateFilters] = useAssessmentsFilters()
  const [page, perPage] = usePageParams({ id: 'asmts' })

  const { data, isRefreshing, isPending } = useAssessments(
    {
      order: asmtsOrder,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    { enabled: open, placeholderData: keepPreviousData }
  )

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="body">
      <DialogTitle>Assessments</DialogTitle>
      <LinearProgress loading={isRefreshing} />
      <DialogContent sx={{ p: 0 }}>
        <RenderControl loading={isPending} isEmpty={data?.length === 0} emptyTitle="No data to display">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sortDirection={asmtsOrder}>
                    <TableSortLabel
                      active
                      direction={asmtsOrder}
                      onClick={() => updateFilters({ asmtsOrder: asmtsOrder === 'desc' ? 'asc' : 'desc' })}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((asmt) => (
                  <TableRow hover key={asmt.id} onClick={() => onSelect(asmt.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>{asmt.name}</TableCell>
                    <TableCell>{toTitleCase(asmt.status)}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{dayjs(asmt.createdAt).format('L')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </RenderControl>
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination id="asmts" loading={isPending} last={data?.length < perPage} />
      </DialogActions>
    </Dialog>
  )
}
